import { useEffect } from 'react';

const SITE_NAME = 'Velox Network';
const DEFAULT_IMAGE = 'https://velox-network-media.s3.eu-west-2.amazonaws.com/Picture+2.png';

/**
 * Sets per-page document metadata for the marketing site.
 *
 * A deliberately small replacement for react-helmet: the marketing pages need
 * a title, description, canonical and social card, and nothing more. Adding a
 * dependency for that would not earn its place in the bundle.
 *
 * Note this runs client-side, so it serves social scrapers that execute JS and
 * improves tab titles and shared-link previews on crawlers that render. It is
 * not a substitute for server-rendered meta if full SEO parity is needed later.
 */

// Reuses an existing tag where possible so we don't accumulate duplicates
// across client-side navigations.
function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useDocumentMeta({ title, description, path, image = DEFAULT_IMAGE }) {
  useEffect(() => {
    const previousTitle = document.title;
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const url = path ? `https://velox-network.com${path}` : undefined;

    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertCanonical(url);

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, path, image]);
}

export default useDocumentMeta;
